import { useEffect, useState, useCallback } from "react";
import { Service } from "../../models/service.model";
import { useService } from "../../contexts/service-context";
import { useEthosNotification } from "../../contexts/ethos-notification-context";
import "./services-configuration.css";
import { useEthosModal } from "../../contexts/ethos-modal-context";
import { useFiles } from "../../contexts/files-context";
import { Files } from "../../models/files.model";
import { useConfigurations } from "../../contexts/configurations-context";
import { useUser } from "../../contexts/user-context";

export type ModalFile = Pick<
  Files,
  "_id" | "filename" | "createdAt" | "expirationDate"
> & { status: string; templateType: string; serviceId: string };

export type UploadNewFile = {
  file: FileList | null;
  filename: string | null;
};

const ServicesConfiguration = () => {
  const [servicesTemplate, setServicesTemplate] = useState<Service[] | null>(
    null,
  );
  const [otherTemplate, setOtherTemplate] = useState<Service[] | null>([]);

  const [filesServices, setFilesServices] = useState<Files[] | null>(null);
  const [typeTemplateConfiguration, setTypeTemplateConfiguration] = useState<
    { value: string; label: string }[] | null
  >(null);

  const {
    getServices,
    updateService,
    uploadServiceTemplate,
    associateServiceTemplate,
  } = useService();

  const { getTemplateUsers, uploadUserTemplate } = useUser();
  const { handleError, showErrorToast, showSuccessToast } =
    useEthosNotification();
  const {
    openServiceTemplatesModal,
    openUploadServiceTemplatesModal,
    openUploadUserTemplatesModal,
  } = useEthosModal();
  const { getFilesInfo, downloadFile } = useFiles();
  const { configurations } = useConfigurations();

  const loadTemplate = useCallback(async () => {
    try {
      const { data, error } = await getServices();
      const { data: templateUsersData, error: templateUsersError } =
        await getTemplateUsers();

      if (error || templateUsersError) {
        handleError(error);
        return;
      }

      setServicesTemplate(data.data.filter((s) => !s.disabled));
      setOtherTemplate([templateUsersData]);
    } catch (err) {
      handleError(err);
    }
  }, [getServices, getTemplateUsers, handleError]);

  const loadFiles = useCallback(async () => {
    try {
      const { data, error } = await getFilesInfo("TEMPLATE");

      if (error) {
        handleError(error);
        return;
      }

      setFilesServices(data.data);
    } catch (error) {
      handleError(error);
    }
  }, [getFilesInfo, handleError]);

  const doDownloadFile = async (file: ModalFile) => {
    const { error } = await downloadFile(file._id);
    if (error) {
      showErrorToast("Errore durante il download del file!");
      return;
    }
  };

  const doLinkFileAndService = async (file: ModalFile, serviceId: string) => {
    const { error } = await associateServiceTemplate(serviceId, file._id);
    if (error) {
      handleError(error);
      return;
    }

    await loadTemplate();
    await loadFiles();

    return true;
  };

  const doArchiveFile = async (file: ModalFile): Promise<unknown> => {
    const found = servicesTemplate?.find(
      (service) => service._id === file.serviceId,
    );
    if (!found) {
      handleError(new Error("Servizio non trovato"));
      return;
    }

    const svr = { ...found };

    if (Array.isArray(svr.templates)) {
      for (const template of svr.templates) {
        if (template.id === file._id) {
          template.status = "ARCHIVED";
          break;
        }
      }
    }

    var { data, error } = await updateService(svr);
    if (error) {
      handleError(error);
      return;
    }

    await loadTemplate();
    await loadFiles();

    showSuccessToast("Servizio aggiornato correttamente!");

    return data;
  };

  const doUploadFile = async (
    { file, filename }: UploadNewFile,
    selectedTypeTemplate: string,
    serviceTypeId: string,
  ): Promise<boolean> => {
    const { error } = await uploadServiceTemplate(
      serviceTypeId,
      selectedTypeTemplate,
      file,
      filename,
    );

    if (error) {
      handleError(error);
      return false;
    }

    await loadTemplate();
    await loadFiles();

    return true;
  };

  const doUploadUserFile = async (
    { file, filename }: UploadNewFile,
    selectedTypeTemplate: string,
  ): Promise<boolean> => {
    console.log("doUploadUserFile", { file, filename, selectedTypeTemplate });
    const { error } = await uploadUserTemplate(
      selectedTypeTemplate,
      file,
      filename,
    );

    if (error) {
      handleError(error);
      return false;
    }

    await loadTemplate();
    await loadFiles();

    return false;
  };

  const openServiceModal = (service: Service) => {
    const modalFile: ModalFile[] = [];

    if (Array.isArray(service.templates)) {
      for (const template of service.templates) {
        const file = filesServices?.find((file) => file._id === template.id);
        if (file) {
          modalFile.push({
            _id: file._id,
            filename: file.filename,
            createdAt: file.createdAt,
            templateType:
              typeTemplateConfiguration?.find(
                (tt) => tt.value === file.fileType,
              )?.label || "N.D.",
            expirationDate: file.expirationDate,
            status: template.status,
            serviceId: service._id,
          });
        }
      }
    }

    openServiceTemplatesModal(
      "customTemplateModal",
      modalFile,
      doDownloadFile,
      doArchiveFile,
    );
  };

  const openUpdateServiceModal = (service: Service) => {
    openUploadServiceTemplatesModal(
      "customTemplateModal",
      service.displayName,
      service._id,
      filesServices,
      doUploadFile,
      doDownloadFile,
      doLinkFileAndService,
    );
  };

  const openUpdateUserTemplateModal = (service: Service) => {
    openUploadUserTemplatesModal(
      "customTemplateModal",
      service.displayName,
      filesServices,
      doUploadUserFile,
      doDownloadFile,
      doLinkFileAndService,
    );
  };

  useEffect(() => {
    if (configurations && configurations.typeTemplate) {
      setTypeTemplateConfiguration(configurations.typeTemplate);
    }

    loadTemplate();
    loadFiles();
  }, [configurations, loadFiles, loadTemplate]);

  if (!Array.isArray(servicesTemplate)) {
    return <h3 className="text-center">Caricamento in corso...</h3>;
  }

  return (
    <>
      <h3 className="text-center">Configurazione template per i servizi</h3>
      <h5 className="text-center pb-3">
        Numero template per i servizi: {servicesTemplate.length}
      </h5>
      <div className="templateConfiguration__root">
        {servicesTemplate.map((service) => (
          <div className="templateConfiguration__card" key={service._id}>
            <div className="templateConfiguration__card--header">
              <h5>{service.displayName}</h5>
              {service.templates?.length > 0 ? (
                <div className="templateConfiguration__card--title">
                  <span>
                    N° template: <strong>{service.templates.length}</strong>
                  </span>
                  <div className="templateConfiguration__card--templates">
                    <span>
                      Attivi:{" "}
                      {service.templates?.filter((t) => t.status === "ACTIVE")
                        .length || 0}
                    </span>
                    {service.templates?.some(
                      (t) => t.status === "ARCHIVED",
                    ) && (
                      <span>
                        Archiviati:{" "}
                        {service.templates?.filter(
                          (t) => t.status === "ARCHIVED",
                        ).length || 0}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                "Nessun template caricato"
              )}
            </div>
            <div className="templateConfiguration__card--body">
              <div className="btn-group" role="group">
                <button
                  id="btnGroup1"
                  type="button"
                  className="btn btn-primary"
                  disabled={service.templates.length === 0}
                  onClick={() => {
                    openServiceModal(service);
                  }}
                >
                  Visualizza
                </button>
                <button
                  id="btnGroup2"
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    openUpdateServiceModal(service);
                  }}
                >
                  Carica
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <hr />
      <h3 className="text-center">Configurazione altri template</h3>
      <h5 className="text-center pb-3">
        Numero altri template: {otherTemplate.length}
      </h5>
      <div className="templateConfiguration__root">
        {otherTemplate &&
          otherTemplate.map((service) => (
            <div className="templateConfiguration__card" key={service._id}>
              <div className="templateConfiguration__card--header">
                <h5>{service.displayName}</h5>
                {service.templates?.length > 0 ? (
                  <div className="templateConfiguration__card--title">
                    <span>
                      N° template: <strong>{service.templates.length}</strong>
                    </span>
                    <div className="templateConfiguration__card--templates">
                      <span>
                        Attivi:{" "}
                        {service.templates?.filter((t) => t.status === "ACTIVE")
                          .length || 0}
                      </span>
                      {service.templates?.some(
                        (t) => t.status === "ARCHIVED",
                      ) && (
                        <span>
                          Archiviati:{" "}
                          {service.templates?.filter(
                            (t) => t.status === "ARCHIVED",
                          ).length || 0}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  "Nessun template caricato"
                )}
              </div>
              <div className="templateConfiguration__card--body">
                <div className="btn-group" role="group">
                  <button
                    id="btnGroup1"
                    type="button"
                    className="btn btn-primary"
                    disabled={service.templates.length === 0}
                    onClick={() => {
                      openServiceModal(service);
                    }}
                  >
                    Visualizza
                  </button>
                  <button
                    id="btnGroup2"
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      openUpdateUserTemplateModal(service);
                    }}
                  >
                    Carica
                  </button>
                </div>
              </div>
            </div>
          ))}
      </div>
    </>
  );
};

export default ServicesConfiguration;
